import { useEffect } from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useIsAuthorized } from '@selfinspectionorg/auth/react';
import { ProfileView } from '@/modules/profile';
import { PageMetadata } from '@/app/PageMetadata';
import { PROFILE_PATH } from '@/app/paths';
import { first, stringifyAppSearch } from '@/app/searchSerialization';

export const Route = createFileRoute('/profile')({
  validateSearch: (search: Record<string, unknown>): { returnUrl?: string } => ({
    returnUrl: first(search.returnUrl),
  }),
  component: RouteComponent,
});

// Exported for the route-guard tests; the router only consumes `Route`.
export function RouteComponent() {
  const isAuthorized = useIsAuthorized();
  const navigate = useNavigate();
  // Loose read so the component also mounts under the ad-hoc test router; the Back control uses it.
  const { returnUrl } = useSearch({ strict: false }) as { returnUrl?: string };

  // The app's authorized page: a live session is required. `AuthProvider` resolves the in-memory
  // access token behind its loader before children render, so a component-level check is enough (see
  // design.md). A guest — or a user whose session ended and re-checks on reload — is sent to login
  // carrying a `returnUrl` back to this page. Any panel `returnUrl` already on the URL (the Back
  // destination) is threaded through so it survives re-login and Back still works. `replace` keeps
  // the unguarded visit out of history so Back does not bounce between login and here.
  useEffect(() => {
    if (!isAuthorized) {
      const profileReturnUrl = returnUrl ? `${PROFILE_PATH}${stringifyAppSearch({ returnUrl })}` : PROFILE_PATH;
      navigate({ to: '/login', search: { returnUrl: profileReturnUrl }, replace: true });
    }
  }, [isAuthorized, navigate, returnUrl]);

  if (!isAuthorized) {
    return null;
  }

  return (
    <>
      <PageMetadata title="Profile | Self Inspection" />
      <ProfileView returnUrl={returnUrl ?? null} />
    </>
  );
}
